import axios from "axios";
import { apiConfig } from "../../config/api";
import i18next from "i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import secureStorage from "../../utils/secureStorage";

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
const API_BASE_URL = apiConfig.basePath;
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
// Helper function to convert time to UTC
const convertTimeToUTC = (localTime, localDate) => {
  if (!localTime) return localTime;
  
  // First convert AM/PM to 24-hour format if needed
  let time24h = localTime;
  if (localTime.includes('AM') || localTime.includes('PM')) {
    const match = localTime.match(/(\d+):(\d+)\s?(AM|PM)/i);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      const ampm = match[3].toUpperCase();
      
      if (ampm === 'PM' && hour !== 12) {
        hour += 12;
      } else if (ampm === 'AM' && hour === 12) {
        hour = 0;
      }
      
      time24h = `${hour.toString().padStart(2, '0')}:${minute}`;
    }
  }
  
  // If no date provided, use today's date for conversion
  const dateToUse = localDate || dayjs().format('YYYY-MM-DD');
  
  // Get user's current timezone
  const userTimezone = dayjs.tz.guess();
  
  // Create a dayjs object in local timezone
  const localDateTime = dayjs.tz(`${dateToUse} ${time24h}`, 'YYYY-MM-DD HH:mm', userTimezone);
  
  // Convert to UTC and return in HH:mm format
  const utcDateTime = localDateTime.utc();
  return utcDateTime.format('HH:mm');
};

// Create Vibe API function
export const createVibe = async (payload) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No access token found");
    }
    
    // Convert time to UTC if it exists
    const processedPayload = {
      ...payload,
      ...(payload.time && { time: convertTimeToUTC(payload.time, payload.date) })
    };
    
    const language = i18next.language === "sp" ? "es" : i18next.language;
    const response = await axios.post(`${API_BASE_URL}create-vibe`, processedPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": language,
        Timezone: userTimeZone,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const getCategories = async () => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No access token found");
    }
    const language = i18next.language === "sp" ? "es" : i18next.language;
    const response = await axios.get(`${API_BASE_URL}category`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Timezone: userTimeZone,
        "Accept-Language": language,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const nearByPlaces = async ({
  latitude,
  longitude,
  radius,
  type,
  language,
}) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No access token found");
    }
    const response = await axios.post(
      `${API_BASE_URL}nearby-places`,
      {
        latitude,
        longitude,
        radius,
        type,
        language,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const getGeoCode = async ({ latitude, longitude }) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No access token found");
    }
    const response = await axios.post(
      `${API_BASE_URL}geocode-address`,
      {
        lat: latitude,
        lng: longitude,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCategoriesCount = async (payload = {}) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) {
      return { error: "No access token found", status: 401 };
    }

    const language = i18next.language === "sp" ? "es" : i18next.language;

    const response = await axios.post(
      `${API_BASE_URL}get-category-count`,
      payload, // <-- 🔥 SEND ACTUAL PAYLOAD HERE
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": language,
        },
      }
    );

    return response;
  } catch (error) {
    if (error?.response?.status === 401) {
      return { error: "Unauthorized", status: 401 };
    }
    return { error: error.message, status: error?.response?.status };
  }
};
